/**
 * Dica sem espera (guia de movimento, ficha "Dica", N2): se outra dica abriu há menos de 500 ms,
 * a próxima aparece na hora; percorrer uma barra de ícones mostra cada dica sem esperar de novo.
 *
 * As dicas [data-dica] do player (audioPlayer.css) e da vitrine (contents.css) são ::after com a
 * espera de 250 ms. Estes eventos, postos no contêiner (.ap, .cs), marcam-no com
 * data-dica-recente enquanto uma dica está aberta e até 500 ms depois que ela fecha; o CSS tira a
 * espera das dicas do contêiner marcado (como a .ds-sem-espera da .ds-dica).
 */
import { useEffect, useRef, type FocusEvent, type PointerEvent } from 'react';
import { tokenMs } from '../../lib/movimento';

/** Ficha "Dica": até 500 ms depois de uma dica fechar, a próxima vem sem espera. */
const SEM_ESPERA_MS = 500;

type Evento = PointerEvent<HTMLElement> | FocusEvent<HTMLElement>;

export interface DicaSemEsperaProps {
  onPointerOver: (e: PointerEvent<HTMLElement>) => void;
  onPointerOut: (e: PointerEvent<HTMLElement>) => void;
  onFocus: (e: FocusEvent<HTMLElement>) => void;
  onBlur: (e: FocusEvent<HTMLElement>) => void;
}

const dicaDe = (alvo: EventTarget | null): Element | null =>
  alvo instanceof Element ? alvo.closest('[data-dica]') : null;

/** Eventos para o contêiner das dicas: `<div className="cs" {...useDicaSemEspera()}>`. */
export function useDicaSemEspera(): DicaSemEsperaProps {
  const estado = useRef({ atual: null as Element | null, abrir: 0, esquecer: 0 });

  useEffect(() => {
    const s = estado.current;
    return () => {
      window.clearTimeout(s.abrir);
      window.clearTimeout(s.esquecer);
    };
  }, []);

  function entrou(e: Evento): void {
    const s = estado.current;
    const dica = dicaDe(e.target);
    if (!dica || dica === s.atual) return;
    const raiz = e.currentTarget;
    s.atual = dica;
    window.clearTimeout(s.abrir);
    const marcar = (): void => {
      window.clearTimeout(s.esquecer);
      raiz.dataset.dicaRecente = '';
    };
    // Outra dica aberta há pouco: esta já aparece sem espera. Senão, conta como aberta depois dela.
    if (raiz.dataset.dicaRecente !== undefined) marcar();
    else s.abrir = window.setTimeout(marcar, tokenMs('--espera-250'));
  }

  function saiu(e: Evento): void {
    const s = estado.current;
    const dica = dicaDe(e.target);
    if (!dica || dica !== s.atual) return;
    // Do botão para o ícone dentro dele: a mesma dica continua aberta.
    if (e.relatedTarget instanceof Node && dica.contains(e.relatedTarget)) return;
    const raiz = e.currentTarget;
    s.atual = null;
    window.clearTimeout(s.abrir);
    if (raiz.dataset.dicaRecente === undefined) return;
    window.clearTimeout(s.esquecer);
    s.esquecer = window.setTimeout(() => {
      delete raiz.dataset.dicaRecente;
    }, SEM_ESPERA_MS);
  }

  return { onPointerOver: entrou, onPointerOut: saiu, onFocus: entrou, onBlur: saiu };
}
